import { Injectable, inject, signal } from '@angular/core';
import { HttpService } from '../http/http.service';
import { DefaultLightningWallet } from 'src/app/model/wallet/default-wallet';
import { JitApi } from '../../api/jit/jit-api';
import { AnalyticsService } from '../analytics/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private httpService = inject(HttpService);
  private analyticsService = inject(AnalyticsService);
  private uid?: string;
  private defaultWalletSignal = signal<DefaultLightningWallet | undefined>(undefined);
  private initialisedSignal = signal<boolean | undefined>(undefined);

  /**
   * Getter for user ID.
   * @returns The user ID if set.
   * @throws Error if the user ID is not set.
   */
  get userId(): string {
    if (!this.uid) {
      throw new Error('ProfileService: No user set');
    }
    return this.uid;
  }

  /**
   * Gets the default wallet signal.
   * @returns The default wallet signal.
   */
  get defaultWallet() {
    return this.defaultWalletSignal.asReadonly();
  }

  /**
   * Gets the initialised signal.
   * @returns The initialised signal.
   */
  get initialised() {
    return this.initialisedSignal.asReadonly();
  }

  /**
   * Initializes the user profile by fetching the Lightning address and nickname.
   * @param id The user ID.
   * @returns A promise that resolves when initialization is complete.
   */
  initialise(id: string): void {
    this.uid = id;
    this.initialisedSignal.set(true);
  }

  /**
   * Sets the user's Lightning address.
   * @param lightningAddress The Lightning address to set.
   * @returns A promise that resolves with the API response.
   */
  async setLightningAddress(operatorId: string, lightningAddress: string): Promise<void> {
    this.defaultWalletSignal.set({
      lightningAddress,
    });

    await JitApi.request(this.httpService, this.analyticsService).setLightningAddress(
      lightningAddress,
    );
  }
}
